import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Header } from '../../Header';
import { useAuth } from '../../../AuthProvider';
import { getUserBatches } from '../../../api/virtualTryonClient';
import { 
  Clock, 
  CheckCircle, 
  XCircle, 
  Loader, 
  Download, 
  Eye,
  RefreshCw,
  Layers
} from 'lucide-react';

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  processing: 'bg-blue-100 text-blue-800 border-blue-200',
  completed: 'bg-green-100 text-green-800 border-green-200',
  failed: 'bg-red-100 text-red-800 border-red-200',
  partially_completed: 'bg-orange-100 text-orange-800 border-orange-200',
};

const STATUS_ICONS = {
  pending: Clock,
  processing: Loader,
  completed: CheckCircle,
  failed: XCircle,
  partially_completed: CheckCircle,
};

export const BatchStatus = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchBatches = useCallback(async (showRefreshIndicator = false) => {
    try {
      if (showRefreshIndicator) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      if (!currentUser) return;

      const headers = {
        Authorization: `Bearer ${await currentUser.getIdToken()}`,
        "X-User-Id": currentUser.uid,
      };

      const response = await getUserBatches({ headers });
      
      if (response && response.batches) {
        // Sort by created_at descending (newest first)
        const sortedBatches = response.batches.sort((a, b) => 
          new Date(b.created_at) - new Date(a.created_at)
        );
        setBatches(sortedBatches);
      }
    } catch (err) {
      console.error('Error fetching batches:', err);
      setError(err.message || 'Failed to load batch jobs');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [currentUser]);

  useEffect(() => {
    fetchBatches();
  }, [fetchBatches]);

  useEffect(() => {
    const hasProcessing = batches.some(b => 
      b.status === 'pending' || b.status === 'processing'
    );

    if (!hasProcessing) return undefined;

    // Auto-refresh every 10 seconds only while jobs are processing
    const interval = setInterval(() => {
      fetchBatches(true);
    }, 10000);

    return () => clearInterval(interval);
  }, [batches, fetchBatches]);

  // Show success message if coming from new batch creation
  useEffect(() => {
    if (location.state?.newBatch && location.state?.batchId) {
      // Clear the state to prevent showing message on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const handleViewBatch = (batchId) => {
    navigate(`/batch-details/${encodeURIComponent(batchId)}`);
  };

  const handleDownloadBatch = async (batchId) => {
    try {
      // This will be handled in the batch details page
      // For now, navigate to details page
      navigate(`/batch-details/${encodeURIComponent(batchId)}`, { state: { autoDownload: true } });
    } catch (err) {
      console.error('Error downloading batch:', err);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  const getStatusText = (status) => {
    const statusMap = {
      pending: 'Pending',
      processing: 'Processing',
      completed: 'Completed',
      failed: 'Failed',
      partially_completed: 'Partially Completed',
    };
    return statusMap[status] || status;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="flex justify-center items-center h-64">
            <Loader className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center">
            <Layers className="w-8 h-8 text-blue-600 mr-3" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Batch Jobs</h1>
              <p className="text-gray-600 mt-1">Track and manage your batch processing jobs</p>
            </div>
          </div>
          
          <button
            onClick={() => fetchBatches(true)}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {location.state?.newBatch && location.state?.batchId && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-green-900">Batch job created successfully!</p>
                <p className="text-sm text-green-700 mt-1">
                  Batch ID: <span className="font-mono">{location.state.batchId}</span> is now being processed.
                </p>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-900">Error loading batch jobs</p>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {batches.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <Layers className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No batch jobs yet</h3>
            <p className="text-gray-600 mb-6">
              Create your first batch job to process multiple garments at once
            </p>
            <button
              onClick={() => navigate('/ai-model')}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Create Batch Job
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Batch ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Input Requests
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Output Images
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {batches.map((batch) => {
                    const StatusIcon = STATUS_ICONS[batch.status] || Clock;
                    const isProcessing = batch.status === 'pending' || batch.status === 'processing';
                    
                    return (
                      <tr 
                        key={batch.batch_id}
                        className="hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => handleViewBatch(batch.batch_id)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-mono text-gray-900">
                            {batch.batch_id ? `${batch.batch_id.substring(0, 16)}...` : 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${STATUS_COLORS[batch.status]}`}>
                            <StatusIcon className={`w-3.5 h-3.5 ${isProcessing ? 'animate-spin' : ''}`} />
                            {getStatusText(batch.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {batch.total_jobs || 0}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm">
                            <span className="text-green-600 font-medium">{batch.completed_jobs || 0}</span>
                            {batch.failed_jobs > 0 && (
                              <span className="text-red-600 font-medium ml-2">
                                / {batch.failed_jobs} failed
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {formatDate(batch.created_at)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewBatch(batch.batch_id);
                              }}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                            >
                              <Eye className="w-4 h-4" />
                              View
                            </button>
                            {batch.status === 'completed' && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownloadBatch(batch.batch_id);
                                }}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors"
                              >
                                <Download className="w-4 h-4" />
                                Download
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BatchStatus;
